/**
 * "Professor Invite Code" panel, shown at the top of a class page to the
 * instructor who owns that section.
 *
 * Only rendered when the signed-in user has an admin-granted teaching grant for
 * THIS section, so students never see it and a professor never sees it on a
 * class they were not scoped to.
 */

import { useState } from 'react';
import {
  useProfessorStatus, useProfessorSections, useInviteCodes, useCreateInviteCode, useSetInviteCodeActive,
} from '../lib/api';
import { Pressable } from '../lib/motion';
import { haptic } from '../lib/haptics';

export function ClassInvitePanel({ sectionId }: { sectionId: string }) {
  // Students are the overwhelming majority of class-page views, so only the
  // cheap status check runs for them; the sections fetch is skipped entirely.
  const { data: status } = useProfessorStatus();
  const canTeach = Boolean(status?.canTeach);
  const { data: sectionsData } = useProfessorSections(canTeach);
  const granted = canTeach && (sectionsData?.sections ?? []).some((s) => s.class_section_id === sectionId);

  const { data: codesData } = useInviteCodes(granted);
  const createCode = useCreateInviteCode();
  const setActive = useSetInviteCodeActive();
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!granted) return null;

  const codes = (codesData?.inviteCodes ?? []).filter((c) => c.class_section_id === sectionId);
  const joinUrl = (code: string) => `https://app.squishyschool.com/join/${code}`;

  const copy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      haptic.success();
      setCopied(key);
      window.setTimeout(() => setCopied(null), 1800);
    } catch {
      setError('Could not copy. Long press the code to select it.');
    }
  };

  return (
    <div className="rounded-2xl border border-squishy-primary/25 bg-squishy-primary/5 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-squishy-primary">
          <path d="M10 2a4 4 0 100 8 4 4 0 000-8zM3 17a7 7 0 1114 0v1H3v-1z" />
        </svg>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-squishy-tealDark">
          Professor invite code
        </span>
      </div>

      {codes.length === 0 ? (
        <>
          <p className="text-sm text-slate-600">
            Create a code and share it with your students. It puts them straight into this exact class and term,
            so nobody lands in the wrong section.
          </p>
          <Pressable
            haptic="medium"
            disabled={createCode.isPending}
            onClick={() => {
              setError(null);
              createCode.mutate({ classSectionId: sectionId }, {
                onError: (e: any) => setError(e?.message ?? 'Could not create a code.'),
              });
            }}
            className="w-full bg-squishy-primary text-white min-h-[44px] rounded-xl text-sm font-semibold shadow-pop disabled:opacity-50"
          >
            {createCode.isPending ? 'Creating...' : 'Create invite code'}
          </Pressable>
        </>
      ) : (
        <div className="space-y-2">
          {codes.map((c) => (
            <div key={c.id} className="rounded-xl bg-white border border-slate-200/80 p-3">
              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={() => copy(c.code, `code-${c.id}`)}
                  className="font-mono text-2xl font-bold tracking-[0.2em] text-squishy-dark"
                  aria-label={`Copy code ${c.code}`}
                >
                  {c.code}
                </button>
                <span className={`text-[10px] font-semibold px-2 py-1 rounded-full ${c.active ? 'bg-teal-50 text-squishy-tealDark' : 'bg-slate-100 text-slate-500'}`}>
                  {c.active ? 'Active' : 'Paused'}
                </span>
              </div>

              <div className="mt-1 text-[11px] text-slate-500">
                {c.redemptions} joined{c.max_uses ? ` of ${c.max_uses} allowed` : ''}
              </div>

              <div className="mt-2 flex gap-2">
                <button
                  onClick={() => copy(joinUrl(c.code), `link-${c.id}`)}
                  className="flex-1 border border-slate-200 min-h-[40px] rounded-lg text-xs font-medium text-slate-600 active:bg-slate-50"
                >
                  {copied === `link-${c.id}` ? 'Link copied' : 'Copy join link'}
                </button>
                <button
                  onClick={() => setActive.mutate({ id: c.id, active: !c.active })}
                  className="flex-1 border border-slate-200 min-h-[40px] rounded-lg text-xs font-medium text-slate-600 active:bg-slate-50"
                >
                  {c.active ? 'Pause' : 'Resume'}
                </button>
              </div>

              {copied === `code-${c.id}` && (
                <div className="mt-1.5 text-[11px] text-squishy-tealDark">Code copied</div>
              )}
            </div>
          ))}

          <button
            onClick={() => createCode.mutate({ classSectionId: sectionId })}
            className="w-full text-xs font-semibold text-squishy-primary min-h-[40px]"
          >
            + Create another code
          </button>
        </div>
      )}

      {error && <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{error}</div>}
    </div>
  );
}
